import { useCallback, useMemo, useRef, useState } from 'react'
import { useSubmit } from '../../hooks/useSubmit'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { createSale, fetchCustomerCredit, fetchStock, fetchVouchers } from '../adminApi'
import type { RequestOptions } from '../../core/types/backend'
import { useAdmin } from '../AdminContext'
import { useRequestId } from '../../hooks/useRequestId'
import { toCents } from '../../core/format/currency'
import { today } from '../../core/format/dates'
import { errorMessage } from '../../core/errors'
import { brandAllowsDiscount, cashProblem, changeCents, creditCentsFor, lineProblem, saleTotalCents, splitProblem, voucherCentsFor } from '../../core/rules/kassa'
import { normalizeVoucherCode } from '../../core/rules/ledger'
import { latestRequest } from '../../utils/latestRequest'
import { paidFromKuur } from './kassaLabels'
import type { KassaAppointment, Sale, SaleLineInput, SalonPaymentMethod } from '../../core/types/kassa'
import type { VoucherValidFor } from '../../core/types/vouchers'
import type { AcquisitionSource } from '../../core/types/insights'
import { useFeatures } from '../../hooks/useFeatures'
import { normalizeSource } from '../../core/rules/acquisition'

/** Eén regel in het concept: de invoer plus wat het scherm erbij toont. */
export type DraftLine = { key: string; input: SaleLineInput; name: string; listPriceCents: number }

/**
 * Voor wie de bon is. `id` leeg = zonder account (gast of losse klant). `isNew`: nog geen herkomst,
 * geen bezoek en geen bestelling; dan vraagt de kassa "Hoe kwam je bij ons?".
 */
export type SaleCustomer = { id?: string; name: string; isNew?: boolean }

export type CheckedVoucher = { last4: string; balanceCents: number; validFor: VoucherValidFor }

/** Hoe ze betaalt. `split` = deels contant, de rest pinnen (naar de server: `method: 'pin'` + `cashPartCents`). */
export type PayWith = 'pin' | 'contant' | 'split'

let seq = 0
const nextKey = () => `regel-${++seq}`

/**
 * Het concept van één bon: klant, regels, cadeaubon en betaling. Rekent met dezelfde regels als de
 * server (core/rules/kassa.ts) om te tonen wat er te betalen is; de server rekent het bij afrekenen
 * opnieuw. Eén `requestId` per inhoud: een herhaalde klik of verloren antwoord rekent nooit dubbel af.
 */
export function useSaleComposer(appointment: KassaAppointment | null) {
  const { catalog, index, site } = useAdmin()
  const kuurSession = appointment ? paidFromKuur(appointment) : false

  const [customer, setCustomer] = useState<SaleCustomer | null>(() =>
    appointment ? { id: appointment.customerId, name: appointment.customerName } : null,
  )
  const [lines, setLines] = useState<DraftLine[]>(() => {
    if (!appointment) return []
    const t = index.treatmentBySlug(appointment.treatmentSlug)
    const list = t?.price != null ? toCents(t.price) : 0
    // Na afronden: de producten die ze meekrijgt (advies voor thuis) staan er al op; weghalen kan.
    // Wat in de salon gebruikt is, hoort bij de behandeling en komt niet op de bon.
    const products: DraftLine[] = (appointment.adviceProductIds ?? []).flatMap((id) => {
      const p = index.productById(id)
      if (!p) return []
      const cents = toCents(p.price)
      return [{ key: nextKey(), name: p.name, listPriceCents: cents, input: { kind: 'product' as const, productId: id, qty: 1, priceCents: cents } }]
    })
    return [
      {
        key: nextKey(),
        name: t?.name ?? appointment.treatmentSlug,
        listPriceCents: list,
        input: { kind: 'treatment', treatmentSlug: appointment.treatmentSlug, priceCents: list, ...(kuurSession ? { fromKuur: true } : {}) },
      },
      ...products,
    ]
  })
  const [voucherCode, setVoucherCode] = useState('')
  const [voucher, setVoucher] = useState<CheckedVoucher | null>(null)
  const [voucherError, setVoucherError] = useState<string | null>(null)
  const [checkingVoucher, setCheckingVoucher] = useState(false)
  const [payWith, setPayWith] = useState<PayWith>('pin')
  const [cashCents, setCashCents] = useState<number | undefined>(undefined)
  // Gesplitst: het contante deel. De rest pint ze.
  const [cashPartCents, setCashPartCents] = useState<number | undefined>(undefined)
  const method: SalonPaymentMethod = payWith === 'contant' ? 'contant' : 'pin'
  const split = payWith === 'split'
  const [touched, setTouched] = useState(false)
  const [source, setSource] = useState<AcquisitionSource | null>(null)
  const { acquisitionQuestion } = useFeatures()
  // Alleen bij een nieuwe klant, en niet vanuit een afspraak (die vroeg het al bij het boeken).
  const askSource = acquisitionQuestion && !appointment && customer !== null && (!customer.id || customer.isNew === true)
  const sourceToSend = askSource ? normalizeSource(source) : null
  const changeCustomer = (next: SaleCustomer | null) => {
    setCustomer(next)
    setSource(null)
    setCreditOn(false)
  }
  // Klanttegoed: alleen bij een klant met account. Gebruiken is een bewuste keuze (standaard uit).
  const [creditOn, setCreditOn] = useState(false)
  const customerId = customer?.id
  const loadCredit = useCallback(
    (opts: RequestOptions) => (customerId ? fetchCustomerCredit(customerId, opts) : Promise.resolve(null)),
    [customerId],
  )
  const { data: credit } = useLatestLoad(loadCredit)
  const { submit, submitting, error, fieldErrors } = useSubmit(createSale)
  // Salonvoorraad, alleen om te waarschuwen: op 0 gaat de verkoop gewoon door (de telling kan fout zijn).
  const { data: stock } = useLatestLoad(fetchStock)
  const salonLevel = (productId: string): number | null => stock?.items.find((i) => i.id === productId)?.levels.salon ?? null

  const inputs = useMemo(() => lines.map((l) => l.input), [lines])
  const totalCents = saleTotalCents(inputs)
  const voucherCents = voucher ? voucherCentsFor(voucher.balanceCents, voucher.validFor, inputs) : 0
  const creditBalanceCents = customerId ? Math.max(0, credit?.balance.cents ?? 0) : 0
  // Wat er van het tegoed af kan op deze bon (na de cadeaubon), en wat er af gaat als ze het gebruikt.
  const creditAvailableCents = creditCentsFor(creditBalanceCents, inputs, voucherCents)
  const creditCents = creditOn ? creditAvailableCents : 0
  const toPayCents = totalCents - voucherCents - creditCents
  // Wat er contant moet komen: alles bij contant, het contante deel bij gesplitst.
  const cashDue = payWith === 'contant' ? toPayCents : split ? (cashPartCents ?? 0) : 0
  const pinPartCents = split ? Math.max(0, toPayCents - (cashPartCents ?? 0)) : 0
  const change = cashDue > 0 && cashCents !== undefined ? changeCents(cashCents, cashDue) : 0

  // ——— Regels ———

  const update = (key: string, fn: (l: DraftLine) => DraftLine) => setLines((all) => all.map((l) => (l.key === key ? fn(l) : l)))
  const setInput = (key: string, patch: Partial<SaleLineInput>) =>
    update(key, (l) => ({ ...l, input: { ...l.input, ...patch } as SaleLineInput }))

  const addTreatment = (slug: string) => {
    const t = index.treatmentBySlug(slug)
    if (!t) return
    const list = t.price != null ? toCents(t.price) : 0
    setLines((all) => [...all, { key: nextKey(), name: t.name, listPriceCents: list, input: { kind: 'treatment', treatmentSlug: slug, priceCents: list } }])
  }

  const addProduct = (id: string) => {
    const p = index.productById(id)
    if (!p) return
    setLines((all) => {
      const same = all.find((l) => l.input.kind === 'product' && l.input.productId === id)
      if (same && same.input.kind === 'product') {
        const qty = same.input.qty + 1
        return all.map((l) => (l.key === same.key ? { ...l, input: { ...same.input, qty } } : l))
      }
      const list = toCents(p.price)
      return [...all, { key: nextKey(), name: p.name, listPriceCents: list, input: { kind: 'product', productId: id, qty: 1, priceCents: list } }]
    })
  }

  const addKuur = (slug: string) => {
    const t = index.treatmentBySlug(slug)
    if (!t?.kuur) return
    const list = toCents(t.kuur.price)
    setLines((all) =>
      all.some((l) => l.input.kind === 'kuur')
        ? all
        : [...all, { key: nextKey(), name: `Kuur ${t.name} · ${t.kuur?.sessions}×`, listPriceCents: list, input: { kind: 'kuur', treatmentSlug: slug, priceCents: list } }],
    )
  }

  /** Een cadeaubon verkopen. Standaard geldig zoals ingesteld (Instellingen › Cadeaus & codes). */
  const addGiftCard = (cents: number) =>
    setLines((all) => [
      ...all,
      { key: nextKey(), name: 'Cadeaubon', listPriceCents: cents, input: { kind: 'giftcard', validFor: site.policy.giftCardValidFor, priceCents: cents } },
    ])

  const removeLine = (key: string) =>
    setLines((all) => {
      const gone = all.find((l) => l.key === key)
      const rest = all.filter((l) => l.key !== key)
      // Kuur weg: een behandeling die als "sessie 1" uit die kuur kwam, wordt weer gewoon afgerekend.
      if (gone?.input.kind === 'kuur' && !kuurSession) {
        return rest.map((l) => (l.input.kind === 'treatment' && l.input.fromKuur ? { ...l, input: { ...l.input, fromKuur: false } } : l))
      }
      return rest
    })

  /** Kan deze regel "uit kuur"? Alleen de behandeling van de afspraak, met een geldige kuur of een kuur op deze bon. */
  const canFromKuur = (l: DraftLine) =>
    l.input.kind === 'treatment' &&
    appointment !== null &&
    l.input.treatmentSlug === appointment.treatmentSlug &&
    (kuurSession || inputs.some((x) => x.kind === 'kuur' && x.treatmentSlug === appointment.treatmentSlug))

  // ——— Cadeaubon ———

  // Volgnummer + code: een antwoord telt alleen als het van het laatste verzoek is én de code nog dezelfde is.
  const voucherRequests = useRef(latestRequest<string>())

  const changeVoucherCode = (code: string) => {
    setVoucherCode(code)
    setVoucher(null)
    setVoucherError(null)
    // Anders getypt: een controle die nog loopt, hoort niet meer bij deze code.
    voucherRequests.current.setKey(normalizeVoucherCode(code) ?? '')
    setCheckingVoucher(false)
  }

  const checkVoucher = async () => {
    const code = normalizeVoucherCode(voucherCode)
    if (!code) {
      setVoucherError('Typ de hele code van de bon, bijv. BB-7K3Q-M9TX-4HPA.')
      return
    }
    const guard = voucherRequests.current
    const ticket = guard.start(code)
    setCheckingVoucher(true)
    setVoucherError(null)
    try {
      const page = await fetchVouchers({ q: code })
      if (!guard.isLatest(ticket, code)) return
      const found = page.items.length === 1 ? page.items[0] : null
      if (!found) setVoucherError('Deze code kennen we niet. Typ de hele code van de bon.')
      else if (found.status === 'verlopen') setVoucherError('Deze cadeaubon is verlopen.')
      else if (found.status === 'leeg') setVoucherError('Deze cadeaubon is al helemaal gebruikt.')
      else if (found.status === 'geblokkeerd') setVoucherError('Deze cadeaubon is geblokkeerd. Open hem bij Cadeaubonnen om te zien waarom.')
      else setVoucher({ last4: found.codeLast4, balanceCents: found.balanceCents, validFor: found.validFor })
    } catch (err) {
      if (guard.isLatest(ticket, code)) setVoucherError(errorMessage(err) ?? 'Controleren lukte niet. Probeer het opnieuw.')
    } finally {
      if (guard.isLatest(ticket, code)) setCheckingVoucher(false)
    }
  }

  // ——— Controleren en afrekenen ———

  /** Mag de prijs van deze regel omlaag? Producten alleen bij een merk met korting (KASSA-ABONNEMENTEN §2). */
  const discountAllowed = (input: SaleLineInput) => {
    if (input.kind !== 'product') return true
    const brand = index.productById(input.productId)?.brand ?? ''
    return brandAllowsDiscount(catalog.brands, brand)
  }
  const lineProblems = Object.fromEntries(lines.map((l) => [l.key, lineProblem(l.input, l.listPriceCents, { discountAllowed: discountAllowed(l.input) })]))
  const needsCustomer = inputs.some((l) => l.kind === 'kuur') && !customer?.id
  const splitIssue = split && toPayCents > 0 ? splitProblem(cashPartCents, toPayCents) : null
  const cashIssue = splitIssue ?? (cashDue > 0 && toPayCents > 0 ? cashProblem(cashCents, cashDue) : null)
  const voucherUseless = voucher !== null && voucherCents === 0 && totalCents > 0
  const onlyGiftCards = inputs.length > 0 && inputs.every((l) => l.kind === 'giftcard')
  const problem =
    lines.length === 0
      ? 'Zet minstens één regel op de bon.'
      : Object.values(lineProblems).find(Boolean) ??
        (customer === null
          ? 'Kies voor wie de bon is.'
          : needsCustomer
            ? 'Een kuur komt op het account van de klant: kies een klant met account.'
            : voucherCode.trim() && !voucher
              ? 'Controleer eerst de cadeaubon, of haal de code weg.'
              : voucherUseless
                ? onlyGiftCards || voucher?.validFor === 'all'
                  ? 'Een cadeaubon kun je niet met een cadeaubon betalen. Haal de code weg.'
                  : 'Deze cadeaubon geldt alleen voor behandelingen.'
                : cashIssue)

  const payload = {
    day: today(),
    ...(customer?.id ? { customerId: customer.id } : customer?.name ? { guestName: customer.name } : {}),
    ...(appointment ? { appointmentId: appointment.id } : {}),
    lines: inputs,
    ...(voucher ? { voucherCode: voucherCode.trim() } : {}),
    ...(creditCents > 0 ? { creditCents } : {}),
    method,
    ...(split && toPayCents > 0 && cashPartCents !== undefined ? { cashPartCents } : {}),
    ...(cashDue > 0 && cashCents !== undefined ? { cashReceivedCents: cashCents } : {}),
    ...(sourceToSend ? { acquisitionSource: sourceToSend } : {}),
  }
  const attempt = JSON.stringify(payload)
  const requestId = useRequestId(attempt)

  const pay = async (): Promise<Sale | null> => {
    setTouched(true)
    if (problem) return null
    return submit({ requestId, ...payload })
  }

  return {
    appointment,
    kuurSession,
    customer,
    setCustomer: changeCustomer,
    askSource,
    source,
    setSource,
    lines,
    lineProblems,
    discountAllowed,
    setInput,
    addTreatment,
    addProduct,
    addKuur,
    addGiftCard,
    removeLine,
    canFromKuur,
    voucherCode,
    changeVoucherCode,
    voucher,
    voucherError,
    checkingVoucher,
    checkVoucher,
    payWith,
    setPayWith,
    cashCents,
    setCashCents,
    cashPartCents,
    setCashPartCents,
    pinPartCents,
    cashDue,
    totalCents,
    voucherCents,
    creditBalanceCents,
    creditAvailableCents,
    creditOn,
    setCreditOn,
    creditCents,
    toPayCents,
    change,
    touched,
    problem,
    submitting,
    error,
    fieldErrors,
    pay,
    catalog,
    salonLevel,
  }
}

export type SaleComposerControl = ReturnType<typeof useSaleComposer>
